// PRE RMA CSB — État d'abonnement et droits d'accès par module.
// Ordre : 1) cache local (IndexedDB, offline/db.ts) lu IMMÉDIATEMENT, 2) serveur dès qu'il répond.
// Chaque réponse valide du serveur est enregistrée dans IndexedDB. Si le serveur est injoignable
// (hors connexion à Ikongo, serveur Render en veille, erreur 5xx), on garde le dernier état connu
// (avec la tolérance hors connexion de shared/business-logic/abonnement.ts) et, à défaut,
// l'état « inconnu » = accès complet : un chef CSB n'est jamais bloqué faute d'avoir pu vérifier.
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, getSession } from '../api/client.ts';
import { enregistrerAbonnementLocal, lireAbonnementLocal } from '../offline/db.ts';
import { accesModules, estPack } from '../../../shared/business-logic/abonnement.ts';
import type { AccesModules, EtatAbonnement, Pack } from '../../../shared/business-logic/abonnement.ts';

export interface AbonnementInfo {
  id: string;
  statut: 'TRIAL' | 'ACTIVE' | 'PENDING_PAYMENT' | 'EXPIRED' | 'SUSPENDED' | 'CANCELLED';
  date_fin_essai: string | null;
  date_fin: string | null;
  prix_mensuel: string;
  devise: string;
  gratuit_jusqu_au: string | null;
  packs: Array<{ pack: string; date_fin: string }>;
}

interface Valeur {
  acces: AccesModules;
  abonnement: AbonnementInfo | null;
  /** true tant que le serveur n'a pas confirmé l'état (appareil hors connexion, serveur en veille ou chargement). */
  horsLigne: boolean;
  chargement: boolean;
  rafraichir: () => Promise<void>;
  /** Applique et met en cache local un état d'abonnement renvoyé par le serveur (ex. réponse d'un paiement déclaré). */
  appliquerAbonnement: (reponse: unknown) => Promise<void>;
}

const TOUT_OUVERT = accesModules(null, '1970-01-01');
const Ctx = createContext<Valeur>({
  acces: TOUT_OUVERT, abonnement: null, horsLigne: false, chargement: false,
  rafraichir: async () => {}, appliquerAbonnement: async () => {},
});

export function dateDuJour(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Accepte { success, abonnement } (nouvelle API) comme l'ancienne forme (abonnement à la racine). Renvoie null si invalide. */
export function extraireAbonnement(reponse: unknown): AbonnementInfo | null {
  const r = reponse as { success?: boolean; abonnement?: unknown } | null | undefined;
  if (!r || r.success === false) return null;
  const a = (r.abonnement ?? r) as Partial<AbonnementInfo> | null;
  if (!a || typeof a !== 'object' || typeof a.id !== 'string') return null;
  return { ...(a as AbonnementInfo), packs: Array.isArray(a.packs) ? a.packs : [], gratuit_jusqu_au: a.gratuit_jusqu_au ?? null };
}

function versEtat(a: AbonnementInfo | null): EtatAbonnement | null {
  if (!a) return null;
  return {
    gratuitJusquAu: a.gratuit_jusqu_au ?? null,
    packs: (a.packs ?? []).filter((p): p is { pack: Pack; date_fin: string } => estPack(p.pack)).map((p) => ({ pack: p.pack, dateFin: p.date_fin })),
  };
}

const pause = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function AbonnementProvider({ children }: { children: ReactNode }) {
  const { csbId, role } = getSession();
  // L'ADMIN n'a pas d'abonnement ; un compte sans CSB non plus : aucun verrou.
  const concerne = !!csbId && role !== 'ADMIN';
  const [abonnement, setAbonnement] = useState<AbonnementInfo | null>(null);
  const [horsLigne, setHorsLigne] = useState(true);
  const [chargement, setChargement] = useState(concerne);
  const [jour, setJour] = useState(dateDuJour());

  const appliquerAbonnement = useCallback(async (reponse: unknown) => {
    const info = extraireAbonnement(reponse);
    if (!info || !csbId) return;
    setAbonnement(info);
    setHorsLigne(false);
    try { await enregistrerAbonnementLocal(csbId, info as unknown as Record<string, unknown>); } catch { /* cache indisponible : sans conséquence */ }
  }, [csbId]);

  const rafraichir = useCallback(async () => {
    if (!concerne || !csbId) { setChargement(false); return; }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setHorsLigne(true); setChargement(false); return; // pas de réseau : on garde le cache
    }
    try {
      let reponse: unknown = null;
      // Un serveur Render en veille met parfois 30 à 60 s à répondre, ou échoue au premier appel : une seule relance.
      for (let essai = 0; essai < 2; essai++) {
        try { reponse = await api.get<unknown>(`/abonnements/${csbId}`); if (extraireAbonnement(reponse)) break; } catch { /* on retente une fois */ }
        if (essai === 0) await pause(4000);
      }
      if (extraireAbonnement(reponse)) await appliquerAbonnement(reponse);
      else setHorsLigne(true); // erreur 4xx/5xx ou réponse invalide : dernier état connu conservé
    } catch {
      setHorsLigne(true);
    } finally {
      setChargement(false);
    }
  }, [concerne, csbId, appliquerAbonnement]);

  useEffect(() => {
    if (!concerne || !csbId) return;
    let actif = true;
    void (async () => {
      try {
        const local = await lireAbonnementLocal(csbId);
        const info = local ? extraireAbonnement(local.abonnement) : null;
        if (actif && info) { setAbonnement((courant) => courant ?? info); setChargement(false); } // droits disponibles sans réseau
      } catch { /* IndexedDB indisponible */ }
      if (actif) await rafraichir();
    })();
    const surReseau = () => { void rafraichir(); };
    const surRetour = () => { setJour(dateDuJour()); if (navigator.onLine) void rafraichir(); };
    window.addEventListener('online', surReseau);
    document.addEventListener('visibilitychange', surRetour);
    return () => { actif = false; window.removeEventListener('online', surReseau); document.removeEventListener('visibilitychange', surRetour); };
  }, [concerne, csbId, rafraichir]);

  const acces = useMemo(
    () => (concerne ? accesModules(versEtat(abonnement), jour, { horsLigne }) : TOUT_OUVERT),
    [concerne, abonnement, jour, horsLigne]
  );

  return <Ctx.Provider value={{ acces, abonnement, horsLigne, chargement, rafraichir, appliquerAbonnement }}>{children}</Ctx.Provider>;
}

export function useAbonnement(): Valeur {
  return useContext(Ctx);
}
